import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import PageState from '../../components/PageState.jsx';
import EmailTab from '../company/EmailTab.jsx';
import IntegrationsTab from '../company/IntegrationsTab.jsx';

/**
 * Notifications > Delivery (ENTRAID-347 redesign, mockup 5, plan C4): how alerts reach people. Moved here from Company
 * Settings because both decide where alerts go:
 *   Sending domain   the Email tab (the customer sending domain), only while the stack feature SendingDomainsEnabled is on;
 *   Ticket integrations  the Integrations tab (Microsoft Teams first), only while IntegrationsEnabled is on.
 * Both keep their own behavior unchanged. `readOnly` is an operator account that is not this tenant's admin: state
 * only, no controls (the API refuses its writes either way), as on Company Settings before.
 */
export default function DeliveryTab({ customerId, readOnly = false }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setView(await api.companySettings(customerId));
    } catch {
      setError('Delivery settings could not be loaded. Try again in a few minutes.');
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <PageState state="error" title="Delivery settings could not be loaded" message={error} onRetry={load} />;
  if (!view) return <p role="status" className="py-8 text-center text-sm text-ipc-muted">Loading delivery settings</p>;

  const emailOn = view.sendingDomainsEnabled === true;
  const integrationsOn = view.integrationsEnabled === true;
  if (!emailOn && !integrationsOn) {
    return (
      <PageState
        state="empty"
        title="Delivery"
        message="Alerts are sent by email from onGuard's own address. A sending domain of your own and ticket integrations appear here when they are available for your deployment."
      />
    );
  }
  return (
    <div className="max-w-3xl space-y-4" data-testid="delivery-tab">
      {emailOn ? <EmailTab customerId={customerId} readOnly={readOnly} /> : null}
      {integrationsOn ? <IntegrationsTab customerId={customerId} view={view} readOnly={readOnly} onChange={setView} /> : null}
    </div>
  );
}
